import React, {useEffect, useRef, useState} from 'react';
import Box from "@mui/material/Box";
import {IconButton, Typography} from "@mui/joy";
import {ArrowBackIosNewRounded} from "@mui-symbols-material/w300";
import {useTheme} from "@mui/joy/styles";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/system/useMediaQuery";
import {useTranslation} from "react-i18next";
import {useMutation} from "@apollo/client";
import {toast} from "react-toastify";
import {Asset, CreateAssetInput, InstalledBaseNodeType, ManualEventType} from "../../codegen/graphql";
import {FormikHelpersRef} from "../../types";
import AssetSearchStep from "./Steps/assetSearch";
import ProductSearch from "./Steps/productSearch";
import {BasicModalContent, CatalogProductNode} from "@dreebit-registry/vsm-microservice-components";
import RegisterForm, {KnownSpecs} from "./Steps/registerForm";
import CreateInstalledBaseEventModal from "../CreateInstalledBaseEventModal";
import InstalledBaseNodePathBreadcrumb from "../InstalledBaseNodePathBreadcrumb";
import NodeTypeSelectionItem from "../InstalledBaseTreeColumn/CreateNodeModal/nodeTypeSelectionItem";
import {SerialLookupProposal} from "./proposalSearchResult";
import {CreateAsset} from "../../graphql/mutations/createAsset";
import {buildAssetInputFromSpecs} from "../../utils/buildAssetInputFromSpecs";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";

type Step = 'SERIAL_SEARCH' | 'PRODUCT_SEARCH' | 'CREATE_ASSET_FORM';

interface Props {
    open?: boolean;
    parentInstalledBaseNodeId?: string;
    /** If provided, jump straight to the install event for this asset. */
    assetId?: string;
    style?: React.CSSProperties;
    onClose?: () => void;
    onComplete?: () => void;
    createNodeCallback?: (nodeId: string) => void;
    isModal?: boolean;
    onBack?: () => void;
    /**
     * The node type being created (ASSET) when opened from the node-type selection.
     * Renders the same change-node-type card as other node types on the search steps;
     * its edit button calls `onBack` (back to the selection).
     */
    nodeType?: InstalledBaseNodeType;
    /**
     * Embed mode: when provided, fires with the chosen / newly-created asset id
     * instead of auto-launching the install-event modal. Used by ASSET_EXCHANGE
     * to pick a replacement asset inside a larger wizard.
     */
    onAssetSelected?: (assetId: string) => void | Promise<void>;
    /** Embed mode: hides this asset id from serial-search results (e.g. the source asset in ASSET_EXCHANGE). */
    excludeAssetId?: string;
    /**
     * Embed mode: while on the create-asset-form sub-step, this ref is populated with a
     * function that triggers the inner Formik submission. The outer wizard can call it
     * (e.g. from a Next-button click) to submit the form remotely so the user only sees
     * one Next button.
     */
    submitTriggerRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}

const RegisterAssetModal = ({
                                open = true,
                                assetId,
                                parentInstalledBaseNodeId,
                                onClose,
                                onComplete,
                                createNodeCallback,
                                isModal = true,
                                onBack,
                                nodeType,
                                onAssetSelected,
                                excludeAssetId,
                                submitTriggerRef,
                            }: Props) => {

    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const {t} = useTranslation();

    const [step, setStep] = useState<Step>('SERIAL_SEARCH');
    const [serialNumber, setSerialNumber] = useState<string>('');
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<CatalogProductNode | null>(null);
    // Date of manufacture from a serial-lookup proposal (systems of record). Bound
    // to the serial the lookup resolved, so it survives picking a sibling product
    // in the browser but is cleared as soon as the user edits the serial again.
    const [proposalDateOfManufacture, setProposalDateOfManufacture] = useState<string | undefined>(undefined);
    // Asset id that should kick off the install-event flow. Either pre-supplied via props,
    // chosen on the serial-search step, or produced by the create-asset form.
    const [eventAssetId, setEventAssetId] = useState<string | undefined>(assetId);

    const [createProposalAsset, {loading: registeringProposal}] = useMutation(CreateAsset);

    const isEmbed = Boolean(onAssetSelected);

    // Embed mode: outer wizard reaches in via `submitTriggerRef` to fire the
    // create-asset form remotely (so the user only sees the outer Next button).
    // The bridge resolves once `handleAssetCreated` has awaited the outer
    // setFieldValue chain, so the wizard's subsequent validation sees the new id.
    const innerFormHelpers = useRef<FormikHelpersRef | null>(null);
    const bridgeResolverRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!submitTriggerRef) return;
        if (step === 'CREATE_ASSET_FORM') {
            submitTriggerRef.current = () => new Promise<void>((resolve, reject) => {
                bridgeResolverRef.current = resolve;
                // Safety net: if CreateAsset fails silently or hangs, give up after
                // a generous window so the wizard isn't stuck waiting forever.
                const timeoutId = setTimeout(() => {
                    if (bridgeResolverRef.current === resolve) {
                        bridgeResolverRef.current = null;
                        reject(new Error('Submission timeout'));
                    }
                }, 30000);
                const settle = (fn: () => void) => {
                    clearTimeout(timeoutId);
                    if (bridgeResolverRef.current === resolve) {
                        bridgeResolverRef.current = null;
                    }
                    fn();
                };
                // Wrap resolve so the timeout is cleared on natural resolution too.
                bridgeResolverRef.current = () => settle(resolve);
                innerFormHelpers.current?.submit().then(async () => {
                    // Validation failed if errors remain after submit attempt — bail.
                    const errors = await innerFormHelpers.current?.validate();
                    if (errors && Object.keys(errors).length > 0) {
                        settle(() => reject(new Error('Form validation failed')));
                    }
                }).catch((err) => settle(() => reject(err)));
            });
        } else {
            submitTriggerRef.current = null;
        }
        return () => {
            if (submitTriggerRef && submitTriggerRef.current) submitTriggerRef.current = null;
        };
    }, [step, submitTriggerRef]);

    const resetState = () => {
        setStep('SERIAL_SEARCH');
        setSerialNumber('');
        setSelectedAsset(null);
        setSelectedProduct(null);
        setProposalDateOfManufacture(undefined);
        setEventAssetId(undefined);
    };

    const handleBack = () => {
        if (step === 'CREATE_ASSET_FORM') {
            // The form is only ever reached from the product search (product picked
            // or "Create manually"), so back always returns there.
            setStep('PRODUCT_SEARCH');
            return;
        }
        if (step === 'PRODUCT_SEARCH') {
            setStep('SERIAL_SEARCH');
            return;
        }
        onBack?.();
    };

    const commitAsset = async (id: string) => {
        if (onAssetSelected) {
            // onAssetSelected may return a Promise (e.g. when the embed parent awaits
            // its Formik setFieldValue). Await so the bridge resolver runs only after
            // the outer field is actually populated.
            await onAssetSelected(id);
            return;
        }
        setEventAssetId(id);
    };

    // Auto-commit: clicking an asset card immediately commits the selection.
    // The brief highlight via selectedAsset state is set first so the card flashes
    // selected before the parent unmounts the step.
    const handleSelectAsset = async (asset: Asset | null) => {
        setSelectedAsset(asset);
        if (asset?.id) {
            await commitAsset(asset.id);
        }
    };

    // Commit the browser selection (Next button) and move to the create-asset form.
    const handleSelectProduct = (product: CatalogProductNode | null) => {
        setSelectedProduct(product);
        if (product) {
            setStep('CREATE_ASSET_FORM');
        }
    };

    const handleNavigateToProductSearch = () => {
        setStep('PRODUCT_SEARCH');
    };

    const handleCreateManually = () => {
        setSelectedProduct(null);
        setStep('CREATE_ASSET_FORM');
    };

    // A typed serial invalidates whatever the last proposal knew about it.
    const handleSerialNumberChange = (value: string) => {
        setSerialNumber(value);
        setProposalDateOfManufacture(undefined);
    };

    // NEEDS_CONFIRMATION: confirm the proposed product in the browser. The
    // canonical serial from the systems of record replaces the typed one so the
    // create-asset form (and registration) uses the authoritative spelling.
    const handleProposalConfirm = (proposal: SerialLookupProposal) => {
        setSelectedProduct(proposal.proposedProductNode);
        setSerialNumber(proposal.serialNumber);
        setProposalDateOfManufacture(proposal.dateOfManufacture ?? undefined);
        setStep('PRODUCT_SEARCH');
    };

    // EXACT: the lookup is certain — register directly from the proposal data,
    // then commit like a clicked search result. On failure, degrade to the
    // NEEDS_CONFIRMATION path so the user isn't stuck on a card that keeps failing.
    const handleProposalRegister = async (proposal: SerialLookupProposal) => {
        try {
            const input: CreateAssetInput = buildAssetInputFromSpecs(KnownSpecs.PRODUCT_KNOWN, {
                productNodeId: proposal.proposedProductNode.id,
                serialNumber: proposal.serialNumber,
                dateOfManufacture: proposal.dateOfManufacture ?? undefined,
            });
            const res = await createProposalAsset({variables: {input}});
            const newAssetId = res.data?.createAsset?.id;
            if (!newAssetId) {
                toast.error(t('Error while creating asset.'));
                handleProposalConfirm(proposal);
                return;
            }
            toast.success(t('Asset created.'));
            await commitAsset(newAssetId);
        } catch (error: any) {
            toast.error(t('Error while creating asset: ') + resolveErrorMessage(error, t));
            handleProposalConfirm(proposal);
        }
    };

    const handleAssetCreated = async (newAssetId: string) => {
        await commitAsset(newAssetId);
        bridgeResolverRef.current?.();
    };

    const handleEventClose = () => {
        setEventAssetId(undefined);
        resetState();
        onComplete?.();
        onClose?.();
    };

    const title = (() => {
        if (isEmbed) return t('Replacement Asset');
        if (step === 'CREATE_ASSET_FORM') {
            if (selectedProduct?.name) return t('Add {{name}}', {name: selectedProduct.name});
            return t('Add Product');
        }
        if (step === 'PRODUCT_SEARCH' && selectedProduct?.name) {
            return t('Add {{name}}', {name: selectedProduct.name});
        }
        return t('Add Asset');
    })();

    // During search steps the content area is locked to a fixed height so the modal stays
    // the same size whether the empty view or a results list is on screen. The form step
    // sizes naturally to its fields.
    const isSearchStep = step === 'SERIAL_SEARCH' || step === 'PRODUCT_SEARCH';

    const stepBody = (() => {
        if (step === 'SERIAL_SEARCH') {
            return <AssetSearchStep
                selectedAssetId={selectedAsset?.id}
                initialQuery={serialNumber}
                onAssetSelect={handleSelectAsset}
                onSerialNumberChange={handleSerialNumberChange}
                excludeAssetId={excludeAssetId}
                onNavigateToProductSearch={handleNavigateToProductSearch}
                onProposalRegister={handleProposalRegister}
                onProposalConfirm={handleProposalConfirm}
                registeringProposal={registeringProposal}
            />;
        }
        if (step === 'PRODUCT_SEARCH') {
            return <ProductSearch
                selectedProduct={selectedProduct}
                onSelectionChange={setSelectedProduct}
                onProductSelect={handleSelectProduct}
                onCreateManually={handleCreateManually}
                // The help toggle explains the drill-down; inside the replacement
                // wizard (embed) the user is mid-flow already.
                showHelp={!isEmbed}
            />;
        }
        // CREATE_ASSET_FORM — a catalog pick links the asset to the productNode
        // (PRODUCT_KNOWN); manual creation falls back to the manufacturer path.
        return <RegisterForm
            product={selectedProduct ?? undefined}
            serialNumber={serialNumber}
            dateOfManufacture={proposalDateOfManufacture}
            knownSpecs={selectedProduct ? KnownSpecs.PRODUCT_KNOWN : KnownSpecs.MANUFACTURER_KNOWN}
            onAssetReady={handleAssetCreated}
            hideSubmitButton={isEmbed}
            setFormikHelpersRef={isEmbed ? (ref) => { innerFormHelpers.current = ref; } : undefined}
        />;
    })();

    const bodyBox = (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            maxWidth: '100%',
            ...(isSearchStep ? {height: 'min(560px, calc(100vh - 240px))'} : {}),
        }}>
            <Box sx={{flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, maxWidth: '100%'}}>
                {stepBody}
            </Box>
        </Box>
    );

    const wizardContent = isEmbed
        ? (
            // Embed wrapper: slim header (back arrow + title, no close icon), no enforced
            // 600px width so the content can fill / center inside the outer wizard.
            <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, maxWidth: '100%', gap: '13px'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: '4px', minHeight: '33px'}}>
                    {step !== 'SERIAL_SEARCH' && (
                        <IconButton onClick={handleBack}>
                            <ArrowBackIosNewRounded fontSize={'small'}/>
                        </IconButton>
                    )}
                    <Typography level={'titleSmall'}>{title}</Typography>
                </Box>
                {bodyBox}
            </Box>
        )
        : (
            <BasicModalContent
                style={{padding: '16px', minWidth: '600px', gap: '13px'}}
                onClose={() => onClose?.()}
                title={
                    <Box className={'flex-row'} sx={{alignItems: 'center', gap: '4px'}}>
                        <Typography level={'titleSmall'}>{title}</Typography>
                    </Box>
                }
                onBack={step !== 'SERIAL_SEARCH' ? handleBack : (onBack ?? undefined)}
            >
                {parentInstalledBaseNodeId && (
                    // mt cancels the BasicModalContent gap so the band sits flush, like the asset form
                    <Box sx={{mt: 0.5, mb: 1}}>
                        <InstalledBaseNodePathBreadcrumb nodeId={parentInstalledBaseNodeId} />
                    </Box>
                )}
                {nodeType && isSearchStep && (
                    <Box sx={{mb: 1}}>
                        <NodeTypeSelectionItem nodeType={nodeType} onEdit={onBack}/>
                    </Box>
                )}
                {bodyBox}
            </BasicModalContent>
        );

    const baseModal = isModal
        ? <Dialog
            open={open && !eventAssetId}
            onClose={onClose}
            fullScreen={isFullScreen}
        >
            {wizardContent}
        </Dialog>
        : (eventAssetId ? null : wizardContent);

    return <>
        {baseModal}
        {eventAssetId && !onAssetSelected && (
            <CreateInstalledBaseEventModal
                open={true}
                assetId={eventAssetId}
                installedBaseNodeId={parentInstalledBaseNodeId ?? null}
                // The register flow's parent node is by definition the install
                // destination — even when it is an asset (sub-asset creation).
                nodeIsInstallTarget
                initialEventType={ManualEventType.ASSET_INSTALL}
                onClose={handleEventClose}
                onSuccess={(info) => {
                    if (info.installedBaseNodeId && createNodeCallback) {
                        createNodeCallback(info.installedBaseNodeId);
                    }
                }}
                refetchQueries={["InstalledBaseNodeList", "AssetBreadcrumbPath", "AssetNameplateInfo", "InstalledBaseEventList"]}
            />
        )}
    </>;
};

export default RegisterAssetModal;
